// avatar-menu.ts — THE avatar menu (owner ruling 2026-09-08): on the Bridge,
// the Helm, the Knowledge Base portal and the API portal, clicking the profile
// picture opens ONE menu — the appearance section on top (Dark/Light segment,
// the style dropdown, the mode's color-scheme dropdown), then the API tokens entry,
// then Log out at the bottom, only when a session exists.
//
// It replaces three implementations that had already drifted from each other:
// the console's theme-control.ts popover behind a gear (plus a separate sun
// button and a separate account circle), the portal's "reproduced EXACTLY"
// clone of it, and the Helm's rail account circle (identity + Log out only,
// its appearance controls living in the settings modal). The gear is RETIRED
// on the Bridge and the portals; on the Helm it opens the settings TAB and
// nothing else. The sun/mode button is retired everywhere — the segment inside
// this menu is the mode control.
//
// One module, four adapters. What a surface hands in:
//   identity   — who is signed in (null on a portal, or a Helm off-console);
//                the avatar still renders (a person glyph) and the menu still
//                opens with appearance + tokens, minus the Log out row;
//   appearance — the surface's seat model: read the seat, list the themes a
//                mode offers under the painted style, list the pickable
//                styles, commit. There is NO hover preview (#3242, owner
//                2026-09-14: "the style picker applies on SELECT only, never on
//                hover") — the menu never calls `preview`/`endPreview`;
//   tokensHref — where "API tokens" goes (the Bridge's #/tokens; absent hides
//                the row);
//   onLogout   — the surface's sign-out lane (absent hides the row);
//   place      — 'below' (a header, opens down-and-left) or 'side' (the Helm's
//                rail, opens up-and-right off a bottom anchor);
//   ids        — a surface's historical element ids, for its specs.
// The CSS is the `avm-component-block`, byte-identical in both stylesheet
// homes (the portals load the Helm's sheet), riding the --cfg-* token seat and
// the --mat-popover-* material tier; primitives-check refuses a drift.
//
// THE TRIGGER IS THE ICON-BUTTON PRIMITIVE (#1155). The circle used to be a
// bare `.avm-btn`: transparent border, transparent fill, `border-radius: 50%`
// in a header and a hand-cut `44x40 / radius 12px` tile in the rail, with its
// own hover (`scale(1.06)`), press and focus ring. Every sibling icon-only
// control in the SAME roots — `#railHelpBtn`, `#railAlertsBtn`,
// `#railSettingsBtn`, `#hcHelpBtn` — rides `.ibtn`, so the account control was
// the one button on those headers that did not follow the style: measured on
// origin/main 29327b16 by e2e/style-parity-probe.mjs, `#railAcctBtn` and
// `#avmBtn` were RED on ALL EIGHT styles x both surfaces (16 of the sweep's 17
// red cells), each naming what the primitive paints in that same root and it
// did not — e.g. under Blueprint `border 1px solid rgb(42, 58, 85) / bg
// rgb(22, 32, 47) / radius 7px` against the trigger's transparent 50%.
//
// So the PRIMITIVE owns the box, the border, the fill, the hover/press chrome,
// the decoration slot and the focus ring; the AVATAR rides inside it unchanged.
// An avatar is not a glyph and is deliberately NOT sized off `--ibtn-glyph`:
// `.avm-avatar` keeps its own 30px (header) / 34px (rail) circle, and only the
// ring around it changes — 32x32 radius 50% -> the style's `ibtn-md`, and
// 44x40 radius 12px -> the style's `ibtn-lg`. The size class is the seam that
// keeps the circle its current size inside the primitive's box.
//
// THREE OWNER DEFECTS, 2026-09-09 (issue #1125), all on this one menu:
//   1. "the menu will render behind a grid making it inaccessible" — the menu
//      was `position: absolute` INSIDE its host, and the Bridge header that
//      hosts it carries `backdrop-filter: var(--mat-shell-blur)`, which is a
//      stacking context under Blueprint (blur 10px) and Glass (6px). A
//      z-index of 200 scoped inside that context loses to ANY positioned
//      sibling of the header at the root level — AG Grid's cells and the
//      synthetic scroll rail (z-index 6) painted straight over the popover's
//      lower half. The menu is now PORTALED to <body> while open (fixed,
//      anchored to the avatar through custom properties the CSS places; the
//      z-ladder token --z-popover), so no host's context can bury it.
//   2. "when I hover over a style choice, it changes the style … it moves the
//      menu … the style drop down disappears" — the hover-preview restamps
//      data-style on <html>, every font kit and padding changes, the header
//      grows or shrinks, the absolute menu moves with it, the option rows in
//      the open list reflow under a pointer that did not move, and the row
//      under the pointer is a DIFFERENT option (or none — mouseleave, the
//      list closes). The fix is geometric: the portaled menu no longer follows
//      the header, its box is PINNED (px width/height) while a list is open,
//      and mkFcSelect pins its list's box and every row's height for the life
//      of an open list when a preview lane is wired. The pointer-to-option map
//      cannot change under a preview. (#3242 then retired the preview itself:
//      a hover paints nothing, so there is no reflow left to chase.)
//   3. "when I change a style, it should automatically change the theme
//      selection to an available choice for the specific style" — a style
//      pick kept the previous style's theme id in the seat; the page painted
//      the new style's default (resolveTheme normalizes) while the theme
//      select's face showed an id its roster did not hold. A style pick now
//      RECONCILES both slots (reconcileSeatForStyle): a slot the new style's
//      roster does not hold moves to that style's default (or first) theme,
//      is persisted through the same commit the pick rides, and the switch is
//      named in a toast through the surface's `notify` seam.

import { IBTN_DECO_HTML } from './buttons.js';
import { mkFcSelect, type FcSkin } from './form-controls.js';

export interface AvatarIdentity {
  email: string;
  name?: string | null;
  pictureUrl?: string | null;
  /** 'admin' renders the role pill. */
  role?: string | null;
}

export interface AppearanceSeatLike {
  theme: 'dark' | 'light';
  themeDark: string;
  themeLight: string;
  style?: string;
}

export interface AvatarThemeOption {
  id: string;
  label: string;
  ui: { bg: string; surface: string; accent: string };
}

export interface AvatarStyleOption {
  id: string;
  label: string;
  /** 'canvas' | 'texture' | 'gradient' | 'flat' — the one-word material hint. */
  background?: string;
}

/** A surface's appearance seat, as this menu reads and writes it. */
export interface AvatarAppearanceModel {
  seat(): AppearanceSeatLike;
  /** The themes offered for a mode UNDER THE PAINTED STYLE (a cross-style
   *  theme would normalize straight back to the style's default — the picker
   *  would appear to do nothing). */
  themesForMode(mode: 'dark' | 'light', styleId: string): AvatarThemeOption[];
  /** The styles a picker may offer right now; one or none hides the row. */
  styles(): AvatarStyleOption[];
  /** The painted style id. */
  styleId(): string;
  /** Persist + paint a whole seat (the segment, both dropdowns land here). */
  commit(next: AppearanceSeatLike): Promise<unknown> | unknown;
  /** @deprecated UNUSED since #3242 (owner 2026-09-14: pickers apply on
   *  SELECT only, never on hover). The menu never calls either; the optional
   *  fields stay only so an adapter that still hands them in keeps compiling
   *  (helm-bridge's portal/theme-control.ts does, at the time of #3242). */
  preview?(next: AppearanceSeatLike): void;
  /** @deprecated UNUSED since #3242 — see `preview`. */
  endPreview?(): void;
  /** A seat change from elsewhere (another tab, the poll) — re-render. */
  onChange?(fn: () => void): () => void;
  /** The label of the theme a mode falls back to when the painted style
   *  registers NO slot for it (R19). Absent = the note names the mode only. */
  fallbackThemeLabel?(mode: 'dark' | 'light'): string | null;
  /** The painted style's display label, for that same note. */
  styleLabel?(): string;
  /** The theme id a style curates as its default for a mode (the registry's
   *  defaultThemeFor). Absent = the first of the style's roster. Read when a
   *  style pick leaves a slot the new style does not offer (#1125 item 3). */
  defaultThemeId?(mode: 'dark' | 'light', styleId: string): string | null | undefined;
  /** The surface's transient message line (its toast). Absent = the theme
   *  switch is still made and still persisted, just not announced. */
  notify?(message: string): void;
}

/** The mode dropdown's label, aria-label and <label> text — one string
 *  (#3242, owner 2026-09-14: "color scheme", not "theme"). */
export function schemeLabel(mode: 'dark' | 'light'): string {
  return `${mode === 'light' ? 'Light' : 'Dark'}-mode color scheme`;
}

/** One theme slot moved by a style pick. */
export interface ThemeMove {
  mode: 'dark' | 'light';
  from: string;
  to: string;
  toLabel: string;
}

/**
 * Reconcile a seat's two theme slots with the style it is about to paint.
 *
 * For each mode: a slot the new style's roster holds is KEPT (the control
 * case); a slot it does not hold moves to the style's curated default when
 * that default is in the roster, else to the roster's first entry; a mode
 * whose roster is EMPTY is left alone — that is R19's dark-only style, where
 * the mode falls back on its own and there is nothing to select. Pure, so the
 * suites drive it with a synthetic catalogue; the menu uses it for the pick
 * AND for the theme select's face, so a seat that already holds a stale id
 * (saved before this existed) shows the theme actually painted rather than
 * a raw id.
 */
export function reconcileSeatForStyle(
  seat: AppearanceSeatLike,
  styleId: string,
  themesForMode: (mode: 'dark' | 'light', styleId: string) => AvatarThemeOption[],
  defaultThemeId?: (mode: 'dark' | 'light', styleId: string) => string | null | undefined,
): { seat: AppearanceSeatLike; moves: ThemeMove[] } {
  const next: AppearanceSeatLike = { ...seat, style: styleId };
  const moves: ThemeMove[] = [];
  for (const mode of ['dark', 'light'] as const) {
    const roster = themesForMode(mode, styleId);
    if (roster.length === 0) continue;
    const slot = mode === 'light' ? next.themeLight : next.themeDark;
    if (roster.some((t) => t.id === slot)) continue;
    const want = defaultThemeId?.(mode, styleId) ?? null;
    const to = (want ? roster.find((t) => t.id === want) : undefined) ?? roster[0]!;
    if (mode === 'light') next.themeLight = to.id; else next.themeDark = to.id;
    moves.push({ mode, from: slot, to: to.id, toLabel: to.label });
  }
  return { seat: next, moves };
}

/** The toast line naming a style pick's theme switch(es). */
export function themeMoveMessage(styleLabel: string, moves: ThemeMove[], labelFor: (id: string) => string | null): string {
  const parts = moves.map((m) => {
    const from = labelFor(m.from) ?? m.from;
    return `${m.mode} theme is now ${m.toLabel} (${from} is not in this style)`;
  });
  return `${styleLabel}: ${parts.join('; ')}`;
}

export interface AvatarMenuIds {
  btn: string;
  menu: string;
  themeSel: string;
  styleSel: string;
  tokens: string;
  logout: string;
}

export const AVM_IDS: AvatarMenuIds = {
  btn: 'avmBtn', menu: 'avmMenu', themeSel: 'avmThemeSel', styleSel: 'avmStyleSel',
  tokens: 'avmTokens', logout: 'avmLogout',
};

export interface AvatarMenuOpts {
  host: HTMLElement;
  identity: AvatarIdentity | null;
  appearance: AvatarAppearanceModel;
  tokensHref?: string | null;
  onLogout?: (() => void) | null;
  place?: 'below' | 'side';
  ids?: Partial<AvatarMenuIds>;
  /** The avatar element (the console hands in its avatarNode so its
   *  .hc-avatar seat keeps painting it); default = this module's circle. */
  avatar?: (identity: AvatarIdentity | null, size: 'sm' | 'lg') => HTMLElement;
  /** The sprite glyph for a session-less avatar (default `crew` — the
   *  roster's person glyph; `user` is not a sprite id, icons:check refuses it). */
  personGlyph?: string;
}

/** The menu's dropdown LAYOUT hooks (paint is the fc-component-block's). */
export const AVM_DD_SKIN = {
  wrap: 'avm-dd',
  btn: 'avm-dd-btn',
  label: 'avm-dd-label',
  chev: 'avm-dd-chev',
  pop: 'avm-dd-pop',
  item: 'avm-dd-item',
} as const satisfies FcSkin;

const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
const svg = (glyph: string): string => `<svg class="icon" aria-hidden="true"><use href="#i-${esc(glyph)}"/></svg>`;

/** Up to two initials from the best name available; '?' rather than an
 *  empty circle so the control is always visibly a person. */
export function avatarInitials(identity: Pick<AvatarIdentity, 'name' | 'email'> | null): string {
  const source = (identity?.name ?? '').trim() || ((identity?.email ?? '').split('@')[0] ?? '');
  if (!source) return '?';
  const words = source.split(/[\s._-]+/).filter(Boolean);
  const first = words[0];
  const last = words[words.length - 1];
  if (!first || !last) return '?';
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?';
}

/** The label beside the circle. */
export function avatarDisplayName(identity: AvatarIdentity | null): string {
  return (identity?.name ?? '').trim() || ((identity?.email ?? '').split('@')[0] || 'Signed in');
}

/** Only absolute http(s) reaches an <img src>. */
export function avatarSafePicture(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return /^https?:\/\//i.test(raw) ? raw : null;
}

/** The module's own circle: picture, then initials, then the person glyph. */
export function avatarNode(identity: AvatarIdentity | null, size: 'sm' | 'lg' = 'sm', personGlyph = 'crew'): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = size === 'lg' ? 'avm-avatar avm-avatar-lg' : 'avm-avatar';
  if (!identity) {
    wrap.innerHTML = svg(personGlyph);
    return wrap;
  }
  const initials = avatarInitials(identity);
  const pic = avatarSafePicture(identity.pictureUrl);
  const fallback = (): void => { wrap.textContent = initials; };
  if (pic) {
    const img = document.createElement('img');
    img.src = pic;
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', fallback);
    wrap.appendChild(img);
  } else {
    fallback();
  }
  return wrap;
}

/** A three-dot swatch of a theme's ground/surface/accent. */
export function themeSwatchHtml(t: AvatarThemeOption): string {
  const dot = (c: string): string => `<span class="avm-tsw-dot" style="background:${esc(c)}"></span>`;
  // No native title=: the option row already carries the theme's NAME beside
  // this swatch, and a browser tooltip is the default the house tooltip layer
  // (data-tip + aria-label) exists to replace.
  return `<span class="avm-tsw">${dot(t.ui.bg)}${dot(t.ui.surface)}${dot(t.ui.accent)}</span>`;
}

/** The one-word material hint beside a style's name. */
export function styleHintHtml(s: AvatarStyleOption): string {
  const hint = s.background === 'canvas' ? 'animated'
    : s.background === 'texture' ? 'textured'
      : s.background === 'gradient' ? 'gradient'
        : 'flat';
  return `<span class="avm-style-hint">${hint}</span>`;
}

/**
 * Mount the avatar menu into `host`. Returns a teardown.
 *
 * Re-renders after every change so the segment's pressed state, the
 * dropdowns' labels and the option swatches all follow the seat rather than
 * tracking it separately — three copies of "which mode are we in" is three
 * chances to disagree.
 */
export function mountAvatarMenu(opts: AvatarMenuOpts): () => void {
  const { host, appearance } = opts;
  const ids: AvatarMenuIds = { ...AVM_IDS, ...(opts.ids ?? {}) };
  const place = opts.place ?? 'below';
  const mkAvatar = opts.avatar ?? ((id, size) => avatarNode(id, size, opts.personGlyph));
  host.classList.add('avm', `avm-place-${place}`);
  let open = false;

  // ---- the PORTAL (#1125 item 1) -----------------------------------------
  // While open, the menu node lives on <body> so no host stacking context
  // (the Bridge header's backdrop-filter, a transformed rail) can bury it
  // under a grid. Placement stays in CSS: this lane hands the anchor's
  // viewport rect and the viewport size over as custom properties and the
  // avm-component-block places `.avm-menu-portal[data-place]` from them, so
  // the phone breakpoint (`side` flips to open UP) is still one media query
  // in one stylesheet, not a second copy of it here.
  let portaled: HTMLElement | null = null;
  const anchorVars = (menu: HTMLElement): void => {
    const r = host.querySelector<HTMLElement>(`#${ids.btn}`)?.getBoundingClientRect?.();
    if (!r) return;
    menu.style.setProperty('--avm-anchor-top', `${r.top}px`);
    menu.style.setProperty('--avm-anchor-right', `${r.right}px`);
    menu.style.setProperty('--avm-anchor-bottom', `${r.bottom}px`);
    menu.style.setProperty('--avm-anchor-left', `${r.left}px`);
    menu.style.setProperty('--avm-vw', `${window.innerWidth}px`);
    menu.style.setProperty('--avm-vh', `${window.innerHeight}px`);
  };
  const onViewportMove = (ev?: Event): void => {
    if (!portaled) return;
    // A scroll INSIDE the menu or inside one of its portaled option lists is
    // not the anchor moving; re-measuring on it would be harmless but noisy.
    const t = ev?.target as (Node & { closest?: Element['closest'] }) | null;
    if (t && (portaled.contains(t) || t.closest?.('.fc-pop-portal'))) return;
    anchorVars(portaled);
  };
  const unportal = (): void => {
    if (!portaled) return;
    portaled.remove();
    portaled = null;
    window.removeEventListener?.('resize', onViewportMove);
    window.removeEventListener?.('scroll', onViewportMove, true);
  };
  const portal = (menu: HTMLElement): void => {
    // A stub document (the smoke) has no body to portal to and no geometry;
    // the menu stays in the host under the in-host placement rules.
    if (!document.body?.appendChild || typeof menu.getBoundingClientRect !== 'function') return;
    anchorVars(menu);
    menu.classList.add('avm-menu-portal');
    menu.dataset.place = place;
    document.body.appendChild(menu);
    portaled = menu;
    window.addEventListener?.('resize', onViewportMove);
    window.addEventListener?.('scroll', onViewportMove, true);
  };
  const menuEl = (): HTMLElement | null => portaled ?? host.querySelector<HTMLElement>(`#${ids.menu}`);

  // ---- the PIN (#1125 item 2) ---------------------------------------------
  // While one of the menu's lists is open, the menu's own box is frozen in
  // px, so nothing that repaints under an open list (a seat change from
  // another tab, the poll) can move the list's trigger — and with it the list
  // the pointer is over. Released when the list closes. (It was built for the
  // style hover-preview, retired by #3242.)
  const pinMenu = (): void => {
    const m = menuEl();
    const r = m?.getBoundingClientRect?.();
    if (!m || !r || !r.width) return;
    m.style.width = `${r.width}px`;
    m.style.height = `${r.height}px`;
    m.style.boxSizing = 'border-box';
  };
  const unpinMenu = (): void => {
    const m = menuEl();
    if (!m) return;
    m.style.width = '';
    m.style.height = '';
    m.style.boxSizing = '';
  };

  function render(): void {
    // A re-render detaches the previous open menu from <body>: the host's
    // innerHTML is about to be rebuilt and a portaled node is not in it.
    unportal();
    const seat = appearance.seat();
    const mode = seat.theme;
    const styleId = appearance.styleId();
    const styles = appearance.styles();
    // R19 — A DARK-ONLY (or light-only) STYLE REGISTERS NO SLOT, and that is
    // the whole mechanism: the mode's dropdown lists nothing and the mode
    // TOGGLE is untouched. An empty control would read as broken, so the row
    // becomes an INFO LINE naming the fallback — never a disabled toggle,
    // which is the shape four earlier attempts got wrong.
    const roster = appearance.themesForMode(mode, styleId);
    const fallback = appearance.fallbackThemeLabel?.(mode) ?? null;
    const identity = opts.identity;
    const tip = identity?.email ?? 'Account';
    host.innerHTML = `
      <button class="avm-btn ibtn ibtn-${place === 'side' ? 'lg' : 'md'}" id="${ids.btn}" type="button"
              data-btn="neutral" data-btn-state="enabled"
              aria-haspopup="menu" aria-expanded="${open}"
              data-tip="${esc(tip)}" aria-label="${esc(tip)}">
        <span data-avatar="btn"></span>${IBTN_DECO_HTML}
      </button>
      <div class="avm-menu${open ? ' on' : ''}" id="${ids.menu}" role="menu"${open ? '' : ' hidden'}>
        <div class="avm-title">Settings</div>
        <div class="avm-modes">
          <button type="button" class="avm-modebtn${mode === 'dark' ? ' on' : ''}" data-btn-role="segment" data-mode="dark">${svg('moon')}<span>Dark</span></button>
          <button type="button" class="avm-modebtn${mode === 'light' ? ' on' : ''}" data-btn-role="segment" data-mode="light">${svg('sun')}<span>Light</span></button>
        </div>
        ${styles.length > 1 ? `
        <label class="avm-label" for="${ids.styleSel}">Style</label>
        <div data-dd="style"></div>` : ''}
        <label class="avm-label" for="${ids.themeSel}">${schemeLabel(mode)}</label>
        ${roster.length === 0 ? `
        <p class="avm-note">${esc(appearance.styleLabel?.() ?? 'This style')} registers no ${mode} slot, so it is not listed here${fallback ? ` — ${mode} mode uses ${esc(fallback)}` : ''}. The Dark/Light toggle stays enabled.</p>`
    : '<div data-dd="theme"></div>'}
        ${opts.tokensHref ? `
        <div class="avm-sep"></div>
        <a class="avm-item" role="menuitem" id="${ids.tokens}" href="${esc(opts.tokensHref)}">${svg('shield')}<span>API tokens</span></a>` : ''}
        ${identity || opts.onLogout ? '<div class="avm-sep"></div>' : ''}
        ${identity ? `
        <div class="avm-who">
          <span data-avatar="head"></span>
          <div class="avm-id">
            <div class="avm-name">${esc(avatarDisplayName(identity))}</div>
            <div class="avm-email">${esc(identity.email)}</div>
          </div>
        </div>
        ${identity.role === 'admin' ? '<div class="avm-role">admin</div>' : ''}` : ''}
        ${opts.onLogout ? `
        <button class="avm-item" role="menuitem" type="button" id="${ids.logout}">${svg('log-out')}<span>Log out</span></button>` : ''}
      </div>`;
    for (const slot of host.querySelectorAll<HTMLElement>('[data-avatar]')) {
      slot.replaceWith(mkAvatar(identity, slot.dataset.avatar === 'head' ? 'lg' : 'sm'));
    }
    wire(seat, mode, styleId, styles);
    // Wire FIRST (the listeners ride the nodes), then lift the open menu out
    // of the host's stacking context.
    if (open) {
      const menu = host.querySelector<HTMLElement>(`#${ids.menu}`);
      if (menu) portal(menu);
    }
  }

  async function commit(next: AppearanceSeatLike): Promise<void> {
    await appearance.commit(next);
    render();
  }

  /** A style pick: reconcile both theme slots against the new style's roster
   *  (#1125 item 3), commit ONCE with the reconciled seat — so the theme
   *  switch is persisted exactly as a user's own theme pick would be — and
   *  name the switch through the surface's notify seam. */
  async function commitStyle(styleId: string, styles: AvatarStyleOption[]): Promise<void> {
    const before = appearance.seat();
    const fromStyle = appearance.styleId();
    const { seat: next, moves } = reconcileSeatForStyle(
      before, styleId,
      (m, s) => appearance.themesForMode(m, s),
      appearance.defaultThemeId ? (m, s) => appearance.defaultThemeId!(m, s) : undefined,
    );
    await commit(next);
    if (moves.length && appearance.notify) {
      // The moved-from id belongs to the PREVIOUS style's roster.
      const labelFor = (id: string): string | null => {
        for (const m of ['dark', 'light'] as const) {
          const hit = appearance.themesForMode(m, fromStyle).find((t) => t.id === id);
          if (hit) return hit.label;
        }
        return null;
      };
      const styleLabel = styles.find((s) => s.id === styleId)?.label ?? styleId;
      appearance.notify(themeMoveMessage(styleLabel, moves, labelFor));
    }
  }

  function wire(seat: AppearanceSeatLike, mode: 'dark' | 'light', styleId: string, styles: AvatarStyleOption[]): void {
    host.querySelector(`#${ids.btn}`)!.addEventListener('click', (ev) => {
      ev.stopPropagation();
      open = !open;
      render();
    });
    for (const b of host.querySelectorAll<HTMLElement>('.avm-modebtn')) {
      b.addEventListener('click', (ev) => {
        ev.stopPropagation();
        // Set the mode the button NAMES rather than flipping the current one:
        // a segmented control that toggles is one refactor away from turning
        // "Dark" into "not dark".
        const want: 'dark' | 'light' = b.dataset.mode === 'light' ? 'light' : 'dark';
        if (want === appearance.seat().theme) return;
        void commit({ ...appearance.seat(), theme: want });
      });
    }
    const themeHost = host.querySelector<HTMLElement>('[data-dd="theme"]');
    if (themeHost) {
      const rowHtml = (t: AvatarThemeOption): string =>
        `${themeSwatchHtml(t)}<span class="avm-dd-item-l">${esc(t.label)}</span>`;
      const withTheme = (s: AppearanceSeatLike, id: string): AppearanceSeatLike =>
        s.theme === 'light' ? { ...s, themeLight: id } : { ...s, themeDark: id };
      const dd = mkFcSelect({
        id: ids.themeSel,
        label: schemeLabel(mode),
        options: () => appearance.themesForMode(appearance.seat().theme, appearance.styleId())
          .map((t) => ({ value: t.id, label: t.label, html: rowHtml(t) })),
        // The face shows the theme actually PAINTED: a seat whose slot the
        // painted style does not hold (saved before #1125's reconciliation)
        // resolves to the style's default, exactly as the paint path does.
        value: () => {
          const s = reconcileSeatForStyle(
            appearance.seat(), appearance.styleId(),
            (m, st) => appearance.themesForMode(m, st),
            appearance.defaultThemeId ? (m, st) => appearance.defaultThemeId!(m, st) : undefined,
          ).seat;
          return s.theme === 'light' ? s.themeLight : s.themeDark;
        },
        // SELECT only (#3242): no onPreview/onPreviewEnd — a hover paints nothing.
        onPick: (id) => { void commit(withTheme(appearance.seat(), id)); },
        onOpen: pinMenu,
        onClose: unpinMenu,
        portal: true,
        skin: AVM_DD_SKIN,
      });
      themeHost.replaceWith(dd.el);
    }
    const styleHost = host.querySelector<HTMLElement>('[data-dd="style"]');
    if (styleHost) {
      const rowHtml = (s: AvatarStyleOption): string =>
        `<span class="avm-dd-item-l">${esc(s.label)}</span>${styleHintHtml(s)}`;
      const sd = mkFcSelect({
        id: ids.styleSel,
        label: 'Style',
        options: () => styles.map((s) => ({ value: s.id, label: s.label, html: rowHtml(s) })),
        value: () => appearance.styleId(),
        // SELECT only (#3242): no onPreview/onPreviewEnd — a hover paints nothing.
        onPick: (id) => { void commitStyle(id, styles); },
        onOpen: pinMenu,
        onClose: unpinMenu,
        portal: true,
        skin: AVM_DD_SKIN,
      });
      styleHost.replaceWith(sd.el);
    }
    host.querySelector(`#${ids.logout}`)?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      open = false;
      render();
      opts.onLogout?.();
    });
    host.querySelector(`#${ids.tokens}`)?.addEventListener('click', () => {
      open = false;
      render();
    });
    // Keep clicks inside the menu from reaching the document dismisser (a
    // re-render detaches the click target, and a detached target reads as
    // outside the host).
    host.querySelector(`#${ids.menu}`)?.addEventListener('click', (ev) => ev.stopPropagation());
    void seat; void styleId;
  }

  const onDocClick = (ev: MouseEvent): void => {
    // The open menu lives on <body>, not in the host — a click inside it is
    // inside the control.
    const t = ev.target as Node;
    if (open && !host.contains(t) && !portaled?.contains(t)) {
      open = false;
      render();
    }
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape' && open) {
      open = false;
      render();
    }
  };
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
  const unsub = appearance.onChange?.(() => render());

  render();
  return () => {
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onKey);
    unsub?.();
    unportal();
    host.classList.remove('avm', `avm-place-${place}`);
    host.innerHTML = '';
  };
}
